import { getReferenceString } from "@medplum/core";
import type { ChargeItem, Procedure } from "@medplum/fhirtypes";

import { Extensions } from "@/fhir/enums/extensions";
import {
  getChargeItemProcedureModifiers,
  hasBillerEditedChargeItemModifiers,
} from "@/utils/billing";

/**
 * Returns the modifiers a charge is billed with: the charge's own once a biller has edited it, and
 * the source Procedure's otherwise.
 *
 * Only a biller editing the charge saves modifiers onto it, and that edit is the most recent word on
 * what is being billed. The Procedure is the encounter's clinical record of what was done, and a
 * biller adjusting how it is billed does not rewrite it. So the two are read in that order rather
 * than merged. A charge nobody has edited carries no modifiers of its own and falls back to the
 * Procedure, which is what charge capture writes today.
 *
 * The marker the edit leaves behind is what decides this, not whether the charge has modifiers on
 * it. A biller who removed every modifier meant to bill the code without any, so an empty edited
 * list is the answer rather than a reason to go back to the Procedure.
 *
 * @param chargeItem The billed charge.
 * @param procedureModifiers The modifiers on the Procedure the charge was captured from, empty when
 *   none resolved.
 * @returns The modifiers to show for the charge.
 */
export function resolveChargeItemModifiers(
  chargeItem: ChargeItem,
  procedureModifiers: readonly string[],
): string[] {
  if (!readsProcedureModifiers(chargeItem)) return getChargeItemProcedureModifiers(chargeItem);

  return [...procedureModifiers];
}

/**
 * Returns whether a charge's modifiers come from its source Procedure, so a caller can skip reading
 * Procedures for charges that already answer for themselves.
 *
 * @param chargeItem The billed charge.
 * @returns True when the charge carries no modifiers of its own and no biller has edited them.
 */
export function readsProcedureModifiers(chargeItem: ChargeItem): boolean {
  return (
    getChargeItemProcedureModifiers(chargeItem).length === 0 &&
    !hasBillerEditedChargeItemModifiers(chargeItem)
  );
}

/**
 * A Procedure's CPT modifiers. Each is its own extension, so a procedure can carry several.
 *
 * @param procedure The Procedure, or undefined when none resolved.
 * @returns The modifiers, empty when the Procedure is missing or records none.
 */
export function getProcedureModifiers(procedure: Procedure | undefined): string[] {
  return (procedure?.extension ?? [])
    .filter((extension) => extension.url === Extensions.ProcedureModifiers)
    .map((extension) => extension.valueString?.trim())
    .filter((modifier): modifier is string => Boolean(modifier));
}

/**
 * The Procedure a charge was captured from.
 *
 * `supportingInformation` is a list because FHIR allows more than one, but capture writes exactly
 * one entry: the source Procedure. So the first entry that resolves is the one. A charge whose
 * Procedure is not in the given set returns undefined instead of throwing, because the clinical
 * record can move on without the billing record and the charge still has to be shown.
 *
 * @param chargeItem The billed charge.
 * @param proceduresByReference The resolved Procedures, indexed by reference string.
 * @returns The source Procedure, or undefined when none of the charge's references resolved.
 */
export function findSourceProcedure(
  chargeItem: ChargeItem,
  proceduresByReference: Map<string, Procedure>,
): Procedure | undefined {
  for (const { reference } of chargeItem.supportingInformation ?? []) {
    const procedure = reference ? proceduresByReference.get(reference) : undefined;
    if (procedure) return procedure;
  }

  return undefined;
}

/**
 * Pairs each charge that reads its modifiers off its source Procedure with that Procedure's
 * modifiers.
 *
 * A charge that answers for its own modifiers, or whose Procedure records none, is left out of the
 * map, so a lookup for it returns undefined and the caller falls back to an empty list.
 *
 * @param chargeItems The charges being shown.
 * @param procedures The source Procedures the charges name.
 * @returns The Procedure modifiers by ChargeItem id.
 */
export function buildProcedureModifiersByChargeItemId(
  chargeItems: ChargeItem[],
  procedures: Procedure[],
): Map<string, string[]> {
  const proceduresByReference = procedures.reduce<Map<string, Procedure>>(
    (byReference, procedure) => {
      const reference = getReferenceString(procedure);
      if (reference) byReference.set(reference, procedure);
      return byReference;
    },
    new Map(),
  );

  return chargeItems.reduce<Map<string, string[]>>((modifiersById, chargeItem) => {
    if (!chargeItem.id || !readsProcedureModifiers(chargeItem)) return modifiersById;

    const modifiers = getProcedureModifiers(findSourceProcedure(chargeItem, proceduresByReference));
    if (modifiers.length) modifiersById.set(chargeItem.id, modifiers);

    return modifiersById;
  }, new Map());
}
